import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { NotificationsService } from '../notification/notification.service';
import { AuthService } from '../auth/auth.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';

export interface TaskUpdatePayload {
  tenantId?: string;
  requestId?: string;
  action?: 'create' | 'update' | 'delete' | string;
  timestamp?: number;
  workflow?: 'tech' | 'exec';
  incomingUserId?: string;
  refreshTargets?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private resolveApiOrigin(): string {
    if (environment.apiUrl) {
      return environment.apiUrl.replace(/\/$/, '');
    }
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'http://localhost:5000';
  }

  private readonly refreshUrl = `${environment.apiUrl}/api/users/refresh-token`;

  private socket: Socket | null = null;
  private connectInFlight = false;
  private authConnectRetries = 0;
  private authWarningLogged = false;
  private readonly maxAuthConnectRetries = 2;

  private connectionStatus = new BehaviorSubject<boolean>(false);
  public connectionStatus$ = this.connectionStatus.asObservable();

  constructor(
    private notificationService: NotificationsService,
    private http: HttpClient,
    @Inject(AuthService) private authService: AuthService
  ) {}

  connect(): void {
    if (!this.authService.isLoggedIn()) {
      return;
    }
    if (this.socket?.connected || this.connectInFlight) {
      return;
    }

    this.connectInFlight = true;
    this.syncSessionCookies()
      .pipe(
        take(1),
        finalize(() => {
          this.connectInFlight = false;
        })
      )
      .subscribe({
        next: () => this.openSocket(),
        error: () => this.openSocket(),
      });
  }

  disconnect(): void {
    this.teardownSocket();
    this.connectionStatus.next(false);
    this.authConnectRetries = 0;
    this.authWarningLogged = false;
  }

  private syncSessionCookies(): Observable<void> {
    return this.http.post(this.refreshUrl, {}, { withCredentials: true }).pipe(
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  private openSocket(): void {
    if (!this.authService.isLoggedIn()) {
      return;
    }
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      this.teardownSocket();
    }

    this.socket = io(this.resolveApiOrigin(), {
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      withCredentials: true,
    });
    this.registerEvents();
  }

  private teardownSocket(): void {
    if (!this.socket) {
      return;
    }
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
  }

  private isAuthConnectError(error: Error): boolean {
    return /authentication error/i.test(error.message);
  }

  private registerEvents(): void {
    if (!this.socket) {
      return;
    }

    this.socket.on('connect', () => {
      this.connectionStatus.next(true);
      this.authConnectRetries = 0;
      this.authWarningLogged = false;
    });

    this.socket.on('disconnect', () => {
      this.connectionStatus.next(false);
    });

    this.socket.on('error', (error: unknown) => {
      console.error('Socket error:', error);
    });

    this.socket.on('inAppNotify', (notification: unknown) => {
      this.notificationService.addNotification(notification);
    });

    this.socket.on('connect_error', (error: Error) => {
      this.connectionStatus.next(false);

      if (!this.isAuthConnectError(error)) {
        return;
      }

      if (
        this.authConnectRetries < this.maxAuthConnectRetries &&
        this.authService.isLoggedIn()
      ) {
        this.authConnectRetries += 1;
        this.teardownSocket();
        this.syncSessionCookies()
          .pipe(take(1))
          .subscribe(() => {
            setTimeout(() => this.openSocket(), 300);
          });
        return;
      }

      if (!this.authWarningLogged) {
        this.authWarningLogged = true;
        if (!environment.production) {
          console.debug(
            'WebSocket authentication failed after retries; real-time updates unavailable until reconnect.',
            error.message
          );
        }
      }
    });
  }

  sendMessageToTenant(tenantId: string, message: string): void {
    this.socket?.emit('sendMessageToTenant', { tenantId, message });
  }

  sendPrivateMessage(userId: string, message: string): void {
    this.socket?.emit('sendPrivateMessage', { userId, message });
  }

  sendMessageToRole(tenantId: string, roleName: string, message: string): void {
    this.socket?.emit('sendMessageToRole', { tenantId, roleName, message });
  }

  getIo(): Socket | undefined {
    return this.socket ?? undefined;
  }

  isConnected(): boolean {
    return !!this.socket?.connected;
  }

  emitTaskUpdate(payload: TaskUpdatePayload): Observable<boolean> {
    return new Observable(observer => {
      if (!this.socket?.connected) {
        observer.error(new Error('Socket not connected'));
        return;
      }

      const tenantId = payload.tenantId != null ? String(payload.tenantId) : '';
      const requestId = payload.requestId != null ? String(payload.requestId) : '';
      if (!tenantId || !requestId) {
        observer.error(new Error('tenantId and requestId are required'));
        return;
      }

      this.socket.emit('sendUpdateMessageToRole', { tenantId, requestId });
      observer.next(true);
      observer.complete();
    });
  }
}
